import { CommonModule } from '@angular/common';
import { Component, inject, signal, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { interval, Subscription, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { LoadingService, NotificationService } from 'src/app/core';
import { Department, Machine } from 'src/app/modules/shared/interface/department.entity';
import { DepartmentService } from 'src/app/modules/shared/services/department.service';

import { NgApexchartsModule } from 'ng-apexcharts';
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexGrid,
  ApexLegend,
  ApexStroke,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  ApexAnnotations,
  ApexFill,
  ChartComponent
} from 'ng-apexcharts';
import { ImpressionDTO, MachineOutputDataDTO, TimeDTO } from 'src/app/modules/shared/interface/machine.entity';
import { MachineService } from 'src/app/modules/shared/services/machine.service';
import { OperationShiftService } from 'src/app/modules/job-operation/services/operation-shift.service';
import { DepartmentDTO } from 'src/app/modules/job-operation/interfaces/operation-shift.entity';
import { MachineTimeService } from 'src/app/modules/job-operation/services/machine-time.service';
import { HoldJobDetailsDTO, MachineHoldDataDTO } from 'src/app/modules/job-operation/interfaces/machine-time.entity';
import { ModalService } from 'src/app/modules/shared/services/modal.service';
import { GlobalDepartmentService } from 'src/app/core/services/global-department.service';

export type MachineChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  stroke: ApexStroke;
  dataLabels: ApexDataLabels;
  grid: ApexGrid;
  legend: ApexLegend;
  tooltip: ApexTooltip;
  annotations: ApexAnnotations;
  fill: ApexFill;
};


@Component({
  selector: 'app-machine-dashboard',
  standalone: true,
  imports: [CommonModule, FormsModule, NgApexchartsModule],
  templateUrl: './machine-dashboard.component.html',
  styleUrls: ['./machine-dashboard.component.scss']
})
export class MachineDashboardComponent implements OnInit, OnDestroy {

  departmentService = inject(DepartmentService);
  loading = inject(LoadingService);
  notification = inject(NotificationService);
  machineService = inject(MachineService);
  operationShiftService = inject(OperationShiftService);
  machineTimeService = inject(MachineTimeService);
  modal = inject(ModalService);
  globalDepartmentService = inject(GlobalDepartmentService);

  protected $$departmentDTO = signal<Department[]>([]);
  protected $$availableMachines = signal<Machine[]>([]);
  protected $$ImpressionDTO = signal<ImpressionDTO[]>([]);
  protected $$TimeDTO = signal<TimeDTO[]>([]);
  protected $$MachineOutputDataDTO = signal<MachineOutputDataDTO[]>([]);
  protected $$DepartmentOutput = signal<DepartmentDTO[]>([]);
  protected $$MachineHoldDataDTO = signal<MachineHoldDataDTO[]>([]);
  protected $$HoldJobDetailsDTO = signal<HoldJobDetailsDTO[]>([]);

  currentTime: string = '';
  showFilters: boolean = false;
  departmentId = this.globalDepartmentService.selectedDepartmentId;
  machineId: number | null = 2;
  today: string = '';
  private clockSub?: Subscription;
  private Today = new Date();
  selectedDate = signal<string>(this.formatDate(new Date()));
  selectedMachineName: string = 'CD 102';
  previousMonth: number = this.Today.getMonth() + 1;
  previousYear: number = this.Today.getFullYear();

  @ViewChild('chart') chart!: ChartComponent;
  public chartOptions!: MachineChartOptions;

  timelineWindowStartHour = signal<number>(8);
  timelineWindowBaseDate = signal<Date>(new Date());
  timelineHeaderStr = signal<string>('(08:00 - 08:00)');

  protected searchParams = signal({
      year: this.Today.getFullYear(),
      month: this.Today.getMonth() + 1,
      day: this.Today.getDate()
  });

  protected holdSearchParams = signal({
      start_date: new Date().toISOString().split('T')[0],
      end_date: new Date().toISOString().split('T')[0]
  });

  constructor() {
    this.fetchDepartment();
  }

  ngOnInit(): void {
    const now = new Date();
    this.today = now.toISOString().split('T')[0];

    this.clockSub = interval(1000).subscribe(() => {
      this.currentTime = new Date().toLocaleTimeString();
    });

    this.initChart();
    // setInterval(() => this.getImpressionDetailsbyMachine(), 60 * 60 * 1000);
    // setInterval(() => this.getTimeDetailsbyMachine(), 60 * 60 * 1000);
    // setInterval(() => this.getChartData(), 60 * 60 *1000);
  }

  ngOnDestroy(): void {
    this.clockSub?.unsubscribe();
  }

  fetchDepartment(): void {
    this.departmentService.getAllDepartments().pipe(
      tap(result => {
        if (result.data?.departments) {
          this.$$departmentDTO.set(result.data.departments);

          const currentDeptId = this.departmentId();
          let selectedDept = result.data.departments.find(d => d.departmentId === currentDeptId);
          
          if (!selectedDept && result.data.departments.length > 0) {
            selectedDept = result.data.departments[0];
            if (selectedDept.departmentId) {
              this.globalDepartmentService.setDepartmentId(selectedDept.departmentId);
            }
          }

          if (selectedDept) {
            this.$$availableMachines.set(selectedDept.machines || []);
            const firstMachineId = selectedDept.machines?.[0]?.machineId ?? null;
            if (firstMachineId !== null) {
              this.onMachineChange(firstMachineId);
            } else {
              this.machineId = null;
            }
          }
        }
      }),
      catchError(err => {
        console.error('Error fetching departments:', err);
        this.notification.set({ type: 'error', message: 'Failed to fetch departments' });
        return of(null);
      })
    ).subscribe();
  }

  onDepartmentChange(deptId: number | null): void {
  if (!deptId) {
    this.$$availableMachines.set([]);
    this.machineId = null;
    return;
  }

  this.globalDepartmentService.setDepartmentId(+deptId);

  const selectedDept = this.$$departmentDTO()
      .find(d => d.departmentId === +deptId);

  if (selectedDept) {
    const machines = selectedDept.machines || [];
    this.$$availableMachines.set(machines);

    const firstMachineId = machines?.[0]?.machineId ?? null;
    this.machineId = firstMachineId;

    if (firstMachineId) {
      this.onMachineChange(firstMachineId);
    }

  } else {
    this.$$availableMachines.set([]);
    this.machineId = null;
  }
}

  onMachineChange(machineId: number): void {
    this.machineId = +machineId;
    const machine = this.$$availableMachines().find(m => m.machineId === this.machineId);
    this.selectedMachineName = machine ? machine.machineName : '';
    this.$$MachineHoldDataDTO.set([]);

    if (machine) {
    this.selectedMachineName = machine.machineName;
  }

    this.getTimeDetailsbyMachine();
    this.getImpressionDetailsbyMachine();
    this.getChartData();
    this.updateChart();
    this.getHoldReasonsByMachine();
    this.getOutPut();
  }

  get completePercentage(): number {
    const denominator = +this.$$ImpressionDTO()[0].plannedImpressions;
    return denominator > 0 ? Math.round(( +this.selectedMachineShiftOutputSum / denominator) * 100) : 0;
  }



  private initChart(): void {
    this.chartOptions = {
      series: [{ name: 'Average Output', data: [] }],
      chart: { type: 'area', height: 250, toolbar: { show: true } },
      xaxis: { categories: [], title: { text: 'Time (HH:mm)' } },
      yaxis: { 
        title: { text: 'Average Output Count' },
        labels: {
          formatter: (val: number) => Math.round(val).toString()
        }
      },
      dataLabels: { enabled: false },
      stroke: { curve: 'smooth' },
      grid: { show: true },
      legend: { show: true },
      tooltip: { enabled: true },
      fill: { type: 'solid', opacity: 0.6 },
      annotations: { xaxis: [] }
    };
  }


  // Convert a single "HH:mm" to a timestamp using the selected searchParams day.
  private timeToTimestampBase(timeString: string): number {
    let hours = 8;
    let minutes = 0;
    const match = String(timeString).match(/(\d{1,2})[:.](\d{2})/);
    if (match) {
        hours = parseInt(match[1], 10);
        minutes = parseInt(match[2], 10);
    } else {
        const fallbackMatch = String(timeString).match(/(\d{1,2})/);
        if (fallbackMatch) {
            hours = parseInt(fallbackMatch[1], 10);
        }
    }
    
    const startH = this.timelineWindowStartHour();
    const baseDate = this.timelineWindowBaseDate();
    const dt = new Date(baseDate);
    dt.setHours(hours, minutes, 0, 0);

    if (hours < startH) {
      dt.setDate(dt.getDate() + 1);
    }

    return dt.getTime();
  }

  private rangeToTimestamps(startStr: string, endStr: string): { start: number; end: number } {
    let start = this.timeToTimestampBase(startStr);
    let end = this.timeToTimestampBase(endStr);

    if (start !== end && end < start) {
      end += 24 * 60 * 60 * 1000;
    }

    const nowTs = Date.now();
    start = Math.min(start, nowTs);
    end = Math.min(end, nowTs);

    return { start, end };
  }


  private updateChart(): void {
    const chartData = this.$$MachineOutputDataDTO()[0];

    const selectedDate = new Date(this.searchParams().year, this.searchParams().month - 1, this.searchParams().day);
    let startTs = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), 8, 0).getTime();
    let endTs = startTs + 24 * 60 * 60 * 1000;
    let startH = 8;

    if (chartData && chartData.windowStart && chartData.windowEnd) {
      if (String(chartData.windowStart).includes('-')) {
        startTs = new Date(String(chartData.windowStart).replace(' ', 'T')).getTime();
        endTs = new Date(String(chartData.windowEnd).replace(' ', 'T')).getTime();
        const startDt = new Date(startTs);
        startH = startDt.getHours();
      } else {
        const matchS = String(chartData.windowStart).match(/(\d{1,2})[:.](\d{2})/);
        if (matchS) {
          startH = parseInt(matchS[1], 10);
          startTs = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), startH, parseInt(matchS[2], 10)).getTime();
        }
        const matchE = String(chartData.windowEnd).match(/(\d{1,2})[:.](\d{2})/);
        if (matchE) {
          endTs = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate(), parseInt(matchE[1], 10), parseInt(matchE[2], 10)).getTime();
          if (endTs <= startTs) endTs += 24 * 60 * 60 * 1000;
        }
      }
    }

    this.timelineWindowStartHour.set(startH);
    this.timelineWindowBaseDate.set(new Date(startTs));

    const formatDt = (d: Date) => {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const HH = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd} ${HH}:${min}`;
    };

    this.timelineHeaderStr.set(`(${formatDt(new Date(startTs))} - ${formatDt(new Date(endTs))})`);

    const annotations: any[] = [];

    if (chartData) {
      annotations.push(
        ...chartData.operationalRanges.map(r => {
          const { start: startTimestamp, end: endTimestamp } = this.rangeToTimestamps(r.x, r.x2);

          return {
            x: startTimestamp,
            x2: endTimestamp,
            fillColor: '#2fb65e',
            opacity: 0.3,
            label: {
              text: `Operational (${r.x}-${r.x2})`,
              style: { fontSize: '10px', color: '#fff', background: '#2fb65e' }
            }
          };
        })
      );

      annotations.push(
        ...chartData.idleRanges.map(r => {const { start: startTimestamp, end: endTimestamp } = this.rangeToTimestamps(r.x, r.x2);

          return {
            x: startTimestamp,
            x2: endTimestamp,
            fillColor: '#a2b62f',
            opacity: 0.3,
            label: {
              text: `Idle (${r.x}-${r.x2})`,
              style: { fontSize: '10px', color: '#fff', background: '#a2b62f' }
            }
          };
        })
      );

      annotations.push(
        ...chartData.maintenanceRanges.map(r => {const { start: startTimestamp, end: endTimestamp } = this.rangeToTimestamps(r.x, r.x2);

          return {
            x: startTimestamp,
            x2: endTimestamp,
            fillColor: '#2f90b6',
            opacity: 0.3,
            label: {
              text: `Maintenance (${r.x}-${r.x2})`,
              style: { fontSize: '10px', color: '#fff', background: '#2f90b6' }
            }
          };
        })
      );

      annotations.push(
        ...chartData.holdRanges.map(r => {const { start: startTimestamp, end: endTimestamp } = this.rangeToTimestamps(r.x, r.x2);

          return {
            x: startTimestamp,
            x2: endTimestamp,
            fillColor: '#bd502f',
            opacity: 0.3,
            label: {
              text: `Hold (${r.x}-${r.x2})`,
              style: { fontSize: '10px', color: '#fff', background: '#bd502f' }
            }
          };
        })
      );

      annotations.push(
        ...chartData.makeReadyRanges.map(r => {const { start: startTimestamp, end: endTimestamp } = this.rangeToTimestamps(r.x, r.x2);

          return {
            x: startTimestamp,
            x2: endTimestamp,
            fillColor: '#f59e0b',
            opacity: 0.3,
            label: {
              text: `Make Ready (${r.x}-${r.x2})`,
              style: { fontSize: '10px', color: '#fff', background: '#f59e0b' }
            }
          };
        })
      );

      if (chartData.heatCoolRanges) {
        annotations.push(
          ...chartData.heatCoolRanges.map(r => {const { start: startTimestamp, end: endTimestamp } = this.rangeToTimestamps(r.x, r.x2);

            return {
              x: startTimestamp,
              x2: endTimestamp,
              fillColor: '#8b5cf6',
              opacity: 0.3,
              label: {
                text: `Heat/Cool (${r.x}-${r.x2})`,
                style: { fontSize: '10px', color: '#fff', background: '#8b5cf6' }
              }
            };
          })
        );
      }
    }

  this.chartOptions = {
    ...this.chartOptions,
    series: [
      {
        name: 'Timeline',
        data: [
          { x: startTs, y: 0 },
          { x: endTs, y: 0 }
        ]
      }
    ],
    xaxis: {
      type: 'datetime',
      title: { text: 'Time (HH:mm)' },
      labels: {
        rotate: -45,
        style: { fontSize: '11px' },
        datetimeUTC: false,
        format: 'HH:mm',
        formatter: function (_: string, timestamp: number) {
          const date = new Date(timestamp);
          const hours = date.getHours().toString().padStart(2, '0');
          const minutes = date.getMinutes().toString().padStart(2, '0');
          return `${hours}:${minutes}`;
        }
      },
      min: startTs,
      max: endTs,
      tickAmount: 24
    },
    annotations: { xaxis: annotations },
    tooltip: { x: { format: 'dd MMM HH:mm' } }
  };

  }


   formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return `${year}-${month}-${day}`;
  }

 onDateChange(event: any) {
    let value = event.target.value;

    if (!value) {
      value = new Date().toISOString().split('T')[0];
      this.today = value;
    }

    const date = new Date(value);
    this.selectedDate.set(value);

    const newYear = date.getFullYear();
    const newMonth = date.getMonth() + 1;
    const newDay = date.getDate();

    this.searchParams.set({
      year: newYear,
      month: newMonth,
      day: newDay
    });

    this.searchParamsNew.set({
      year: newYear,
      month: newMonth,
      department: this.departmentId() ?? 2
    });

    this.getImpressionDetailsbyMachine();
    this.getTimeDetailsbyMachine();
    this.getChartData();
    this.updateChart();

    if (newMonth !== this.previousMonth || newYear !== this.previousYear) {
      this.getOutPut();
      this.previousMonth = newMonth;
      this.previousYear = newYear;
    }
  }


  getImpressionDetailsbyMachine() {
    this.loading.set(true);
    if(this.machineId) {
      this.machineService.getDailyReport(this.machineId, this.searchParams())
        .pipe(
          tap(response => {
            this.loading.set(false);
            this.$$ImpressionDTO.set([response.data])
          }),
          catchError(error => {
            this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to fetch machine daily Impression data` });
                console.error(error);
                return of(null);
          })
        ).subscribe();
    }
  }

  getTimeDetailsbyMachine() {
    this.loading.set(true);
    if(this.machineId) {
      this.machineService.getDailyOperationTime(this.machineId, this.searchParams())
        .pipe(
          tap(response => {
            this.loading.set(false);
            this.$$TimeDTO.set([response.data])
          }),
          catchError(error => {
            this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to fetch machine daily Impression data` });
                console.error(error);
                return of(null);
          })
        ).subscribe();
    }
  }

  convertDecimalHoursToHrsMins(decimalHours: number): { hours: number; minutes: number } {
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    return { hours, minutes };
  }

  getChartData() {
    this.loading.set(true);
    if (this.machineId) {
      this.$$MachineOutputDataDTO.set([]);

      this.machineService.getDailyChartData(this.machineId, this.searchParams())
        .pipe(
          tap(response => {
            this.loading.set(false);
            if (response?.data) {
              this.$$MachineOutputDataDTO.set([response.data]);
            } else {
              this.$$MachineOutputDataDTO.set([]);
            }
            this.updateChart();
            // console.log(this.$$MachineOutputDataDTO());
            
          }),
          catchError(error => {
            this.loading.set(false);
            this.$$MachineOutputDataDTO.set([]);
            this.updateChart();
            this.notification.set({ type: 'error', message: `Failed to fetch machine daily chart data` });
            console.error(error);
            return of(null);
          })
        )
        .subscribe();
    }
  }

  //new integrate

   protected searchParamsNew = signal({
        year: new Date().getFullYear(),
        month: new Date().getMonth() + 1,
        department: this.departmentId() ?? 2
    });

  getOutPut() {
        this.loading.set(true);
        this.operationShiftService.getShiftOutputByDep(this.searchParamsNew()).pipe(
            tap(response => {
                this.loading.set(false);
                this.$$DepartmentOutput.set([response.data]);
            }),
            catchError(error => {
                this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to fetch shift Output data` });
                console.error(error);
                return of(null);
            })
        ).subscribe();
    }

    get selectedMachineShiftOutputSum(): number {
      const departmentData = this.$$DepartmentOutput()[0]; 
      if (!departmentData) return 0;

      const machine = departmentData.machines.find(m => m.machineId === this.machineId);
      if (!machine) return 0;

      const dailyData = machine.dailyShiftCounts.find(d => d.date === this.selectedDate());
      if (!dailyData) return 0;

      return dailyData.shifts.reduce((sum, shift) => sum + shift.noOfSheets, 0);
    }

  getHoldReasonsByMachine() {

    const { start_date, end_date } = this.holdSearchParams();
    if (!start_date || !end_date) {
      this.notification.set({
        type: 'alert',
        message: 'Please fill the date range before fetching hold times.'
      });
      return;
    }

    this.loading.set(true);
    if(this.machineId) {
      this.machineTimeService.findHoldReasonsByMachine(this.machineId, this.holdSearchParams())
        .pipe(
          tap(response => {
            this.loading.set(false);
            this.$$MachineHoldDataDTO.set([response.data]);
            
            // console.log(this.$$MachineHoldDataDTO())
            // console.log(this.$$HoldJobDetailsDTO());
            
          }),
          catchError(error => {
            this.loading.set(false);
                this.notification.set({ type: 'error', message: `Failed to fetch machine hold reason for the selected range` });
                console.error(error);
                return of(null);
          })
        ).subscribe();
    }
  }

  onDateChangeHold(field: 'start_date' | 'end_date', value: string): void {
    this.holdSearchParams.update(params => ({
      ...params,
      [field]: value
    }));
  }

  convertToHoursAndMinutes(timeDecimal: number): string {
      if (timeDecimal == null || isNaN(timeDecimal)) return '0h 0m';

      const hours = Math.floor(timeDecimal);
      const minutes = Math.round((timeDecimal - hours) * 60);

      return `${hours}h ${minutes}mins`;
    }

    getHoldReasonSummary(): { reason: string, time: number, jobs: HoldJobDetailsDTO[] }[] {
      const data = this.$$MachineHoldDataDTO();
      if (!data || !data.length) return [];

      const summaryMap: Record<string, { time: number; jobs: HoldJobDetailsDTO[] }> = {};

      data[0].dailyHoldReasons.forEach(day => {
        day.reasons.forEach(r => {
          if (!summaryMap[r.reason]) {
            summaryMap[r.reason] = { time: 0, jobs: [] };
          }

          summaryMap[r.reason].time += r.time;

          summaryMap[r.reason].jobs.push(...r.jobs);
        });
      });

      return Object.entries(summaryMap).map(([reason, { time, jobs }]) => ({ reason, time, jobs }));
    }

    getTotalHoldTime(): number {
      return this.getHoldReasonSummary().reduce((sum, r) => sum + r.time, 0);
    }

    getTotalIdleTime(): number {
      const data = this.$$MachineHoldDataDTO();
      if (!data || !data.length) return 0;

      return data[0].dailyHoldReasons.reduce((sum, day) => sum + (day.idleTime || 0), 0);
    }

    getTotalMachineDowntime(): number {
      return this.getTotalHoldTime();
    }

    openHoldJobDetails() {
      this.modal.$$holdJobDetails.set(true);
    }

    // Track which jobs are expanded
expandedJobs = new Set<string>();

toggleJob(jobNo: string) {
  if (this.expandedJobs.has(jobNo)) {
    this.expandedJobs.delete(jobNo);
  } else {
    this.expandedJobs.add(jobNo);
  }
}

isJobExpanded(jobNo: string): boolean {
  return this.expandedJobs.has(jobNo);
}

getSelectedDepartmentName(): string {
      const dept = this.$$departmentDTO()
          ?.find(d => d.departmentId == this.departmentId());

      return dept ? dept.departmentName : 'Select Department';
  }

cleanOperators(operators: string): string {
  if (!operators) return '';
  return operators.replace(/\(\d+\)/g, '').trim();
}


}
